import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { useCreateObservation, useMultipleFileUpload } from '../../hooks/api';
import { useModal } from '../../stores/uiStore';
import { useToast } from '../ui/ToastProvider';
import { ObservationCreate } from '../../types/api';
import EnhancedObservationForm from './EnhancedObservationForm';
import { Body, Spinner } from '../ui';

interface CreateObservationModalProps {
  defaultChildId?: string;
}

const CreateObservationModal: React.FC<CreateObservationModalProps> = ({ 
  defaultChildId 
}) => {
  const modal = useModal('create-observation');
  const createMutation = useCreateObservation();
  const { uploadFiles, isUploading } = useMultipleFileUpload();
  const { addToast } = useToast();
  const [uploadProgress, setUploadProgress] = useState<{ completed: number; total: number } | null>(null);
  
  const handleSubmit = async (data: ObservationCreate & { media?: File[] }) => {
    try {
      const { media, ...observationData } = data;
      const newObservation = await createMutation.mutateAsync(observationData);
      
      // Handle media upload after observation creation
      if (media && media.length > 0 && newObservation.id) {
        setUploadProgress({ completed: 0, total: media.length });
        
        await uploadFiles(
          newObservation.id,
          media,
          (progress) => setUploadProgress(progress)
        );
      }
      
      // Reset progress and close modal
      setUploadProgress(null);
      addToast({
        type: 'success',
        title: 'Success',
        message: 'Observation created successfully!',
      });
      modal.close();
    } catch (error) {
      console.error('Failed to create observation:', error);
      setUploadProgress(null);
      addToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to create observation. Please try again.',
      });
    }
  };
  
  const handleCancel = () => {
    if (!createMutation.isPending && !isUploading) {
      modal.close();
    }
  };
  
  const isProcessing = createMutation.isPending || isUploading;
  
  return (
    <Modal
      isOpen={modal.isOpen}
      onClose={handleCancel}
      size="xl"
      closeOnEscape={!isProcessing}
      closeOnOverlayClick={!isProcessing}
      showCloseButton={!isProcessing}
    >
      <div className="p-6">
        {createMutation.isPending && (
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <Spinner size="sm" />
              <Body className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Creating observation...
              </Body>
            </div>
          </div>
        )}

        {uploadProgress && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <Body className="text-sm font-medium text-green-900 dark:text-green-100">
                Observation created! Uploading media files...
              </Body>
              <Body className="text-sm text-green-700 dark:text-green-300">
                {uploadProgress.completed} / {uploadProgress.total}
              </Body>
            </div>
            <div className="w-full bg-green-200 dark:bg-green-800 rounded-full h-2">
              <div
                className="bg-green-600 dark:bg-green-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(uploadProgress.completed / uploadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        )}
        
        <EnhancedObservationForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isProcessing}
          defaultChildId={defaultChildId}
        />
      </div>
    </Modal>
  );
};

export default CreateObservationModal;
